import { cn } from "@/lib/cn";
import type { DeliveryStats } from "@/api";

const SEGMENTS = [
  { key: "delivered", label: "zugestellt", className: "bg-green" },
  { key: "bounced", label: "Rückläufer", className: "bg-amber" },
  { key: "failed", label: "abgelehnt / fehlgeschlagen", className: "bg-danger" },
  { key: "unknown", label: "unbekannt", className: "bg-danger opacity-40" },
  { key: "queued", label: "noch offen", className: "bg-accent" },
] as const;

function segmentValues(stats: DeliveryStats): Record<(typeof SEGMENTS)[number]["key"], number> {
  return {
    delivered: stats.delivered,
    bounced: stats.bounced,
    failed: stats.rejected + stats.failed,
    unknown: stats.unknown,
    queued: stats.queued,
  };
}

/** Not arrived: rejected, failed, unknown (bounces are counted separately). */
export function failedCount(stats: DeliveryStats): number {
  return stats.rejected + stats.failed + stats.unknown;
}

export function formatPercent(part: number, total: number): string {
  if (total <= 0) return "–";
  return `${((part / total) * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
}

export function DeliveryStatsBar({
  stats,
  className,
  size = "sm",
}: {
  stats: DeliveryStats;
  className?: string;
  size?: "sm" | "lg";
}) {
  const values = segmentValues(stats);
  const label = SEGMENTS.map((segment) => `${values[segment.key]} ${segment.label}`).join(", ");
  return (
    <div
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        "flex overflow-hidden rounded-full bg-surface-subtle",
        size === "lg" ? "h-2.5" : "h-1.5",
        className,
      )}
    >
      {stats.total > 0
        ? SEGMENTS.filter((segment) => values[segment.key] > 0).map((segment) => (
            <span
              key={segment.key}
              className={cn("h-full", segment.className)}
              style={{ width: `${(values[segment.key] / stats.total) * 100}%` }}
            />
          ))
        : null}
    </div>
  );
}

export function DeliveryStatsLegend({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted", className)}>
      {SEGMENTS.map((segment) => (
        <span key={segment.key} className="inline-flex items-center gap-1.5">
          <span className={cn("inline-block h-2 w-2 rounded-sm", segment.className)} />
          {segment.label}
        </span>
      ))}
    </div>
  );
}
